import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/**
 * `/notifications/unsubscribe` — the link in every digest's footer.
 *
 * The **fifth** public page, on exactly the terms S-B8 set for the other four:
 * it is opened by whoever holds the mailbox, who may not be signed in, on a
 * device that may not be theirs — so no guard — and it acts on a **POST**, never
 * on page load, because mail security scanners fetch every URL in a message
 * before a human sees it. A GET that unsubscribed would mean every digest
 * AugRank sends unsubscribing its own recipient.
 *
 * **It asks first, unlike the other four.** They redeem automatically because
 * the person clicking has already decided and the token is single-use. This one
 * affects *everybody* in the organisation — notification preferences are per
 * tenant (`app/db/models/tenant.py` states why) — so the page says what it will
 * do and waits for the button. That is also why the token does not expire and
 * is not single-use: an unsubscribe link that had stopped working would be worse
 * than none, and pressing it twice is a no-op.
 */
export function Unsubscribe(): JSX.Element {
  const token = useToken()
  const [done, setDone] = useState<string | null>(null)

  const unsubscribe = useMutation((value: string) =>
    call(() =>
      api.POST('/api/v1/notifications/unsubscribe', { params: { query: { token: value } } }),
    ),
  )

  if (!token) {
    return (
      <PublicPage title="That link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The unsubscribe link needs the token from the email. Copy the whole address out of the
            message, or change which events send email in AugRank.
          </p>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Link className="ar-btn ar-btn--ghost" to="/settings/notifications">
            Open notification settings
          </Link>
        </div>
      </PublicPage>
    )
  }

  if (done) {
    return (
      <PublicPage title="Email turned off">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            AugRank will stop emailing <strong>{done}</strong> about activity. Nobody in the
            organisation will receive digests or alerts by email.
          </p>
          <p className="ar-muted">
            Security and account emails — password resets, verification links — are still sent.
            Those are not notifications and cannot be turned off.
          </p>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Link className="ar-btn ar-btn--primary" to="/settings/notifications">
            Turn some back on
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage title="Stop AugRank emails?">
      <div className="ar-card__body ar-stack">
        <InlineError failure={unsubscribe.state.failure} />
        <p style={{ marginTop: 0 }}>
          This turns off <strong>every notification email for the whole organisation</strong> — not
          just for you. Anyone else who administers it will stop receiving digests and alerts too.
        </p>
        <p className="ar-muted">
          The notification centre inside AugRank is unaffected, and you can switch individual events
          back on at any time in Settings → Notifications.
        </p>
      </div>
      <div className="ar-card__footer">
        <Link className="ar-btn ar-btn--ghost" to="/settings/notifications">
          Change individual events instead
        </Link>
        <span className="ar-card__spacer" />
        <Button
          tone="danger"
          busy={unsubscribe.pending}
          busyLabel="Turning off"
          onClick={() => {
            void (async () => {
              const result = await unsubscribe.run(token)
              if (result.ok) setDone(result.data.organisation)
            })()
          }}
        >
          Turn off email
        </Button>
      </div>
    </PublicPage>
  )
}
